'use client';

import React, { useState, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { BodySmall, ParagraphSmallText } from '../Texts';
import { TextInputContainer, StyledInput } from '../Inputs/Text';
import { useSolana } from '../../hooks/useSolana';
import Button from '../Button';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTokenBySymbol,
  selectTokensWithUSDBalance,
} from '../../redux/slices/tokenSlice';
import {
  selectTokenBalance,
  selectWalletTokenBalance,
  selectLastRefreshTime,
} from '../../redux/slices/walletSlice';
import { openModal } from '../../redux/slices/modals';
import { useGetTokenPriceQuery } from '../../redux/api/public/tokens';
import { useFlag } from '@unleash/proxy-client-react';
import { UNLEASH_CALCULATE_CAUSE_FEE } from '../../constants';
import TokenSwapInputs from '../TokenSwapInputs';
import { formatTokenPrice } from '../../common/currency';
import { usePrivy } from '@privy-io/react-auth';
import ContentLoader from 'react-content-loader';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive.js';
import {
  EVENT_DEPOSIT_MODAL_OPENED,
  EVENT_TOKEN_SWAP,
  sendEvent,
  TOKEN,
} from '../../common/analytics';
import { hapticsImpactLight } from '../../common/haptic';
import useCustomLogin from '../../hooks/useCustomLogin';
import { toast } from 'react-toastify';

export const TokenLogo = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 12px;
`;

export const TokenInput = styled(TextInputContainer)<{ $isFocused?: boolean }>`
  display: flex;
  align-items: center;
  border-radius: 8px;
  height: 76px;
  border: 1px solid
    ${({ theme, $isFocused }) =>
      $isFocused ? theme.primary.primary : 'transparent'};
  box-shadow: ${({ theme, $isFocused }) =>
    $isFocused ? `0 0 0 0.5px ${theme.primary.primary}` : 'none'};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
`;

export const Line = styled.div`
  margin: 16px 0;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
`;

const PriceInfo = styled.div`
  margin: 16px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AdvancedSettingsButton = styled(PriceInfo)`
  cursor: pointer;
  margin: 16px 0;
  @media (hover: hover) {
    &:hover {
      opacity: 0.8;
    }
  }
`;

const AdvancedSettingsContent = styled.div`
  padding: 0px 12px 12px 12px;
  border-radius: 8px;
`;

const SlippageButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const SlippageButton = styled.button<{ $active?: boolean }>`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.primary.primary : theme.text.title};
  background: ${({ theme, $active }) =>
    $active ? theme.primary.lightest : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.primary.primary : theme.text.title};
  cursor: pointer;
  font-size: 14px;
  box-shadow: ${({ theme, $active }) =>
    $active ? `0 0 0 0.5px ${theme.primary.primary}` : 'none'};

  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme }) => theme.primary.primary};
    }
  }
`;

const CustomSlippageInputContainer = styled(TextInputContainer)<{
  $active?: boolean;
}>`
  width: 80px;
  background: ${({ theme, $active }) =>
    $active ? theme.primary.lightest : 'transparent'};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.primary.primary : theme.text.title};
  border-radius: 6px;
  height: 100%;
  padding: 0;
  gap: 0;
  box-shadow: ${({ theme, $active }) =>
    $active ? `0 0 0 0.5px ${theme.primary.primary}` : 'none'};

  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme }) => theme.primary.primary};
    }
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.primary.primary};
    box-shadow: 0 0 0 0.5px ${({ theme }) => theme.primary.primary};
  }
`;

const CustomSlippageStyledInput = styled(StyledInput)<{ $active?: boolean }>`
  text-align: right;
  padding: 6px 12px;
  font-size: 14px;
  background: transparent;
  color: ${({ theme, $active }) =>
    $active ? theme.primary.primary : theme.text.title};
`;

const CustomSlippageInput = ({
  value,
  onChange,
  $active,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  $active?: boolean;
}) => {
  return (
    <CustomSlippageInputContainer $active={$active}>
      <CustomSlippageStyledInput
        type='number'
        value={value}
        onChange={onChange}
        placeholder='Custom'
        $active={$active}
      />
    </CustomSlippageInputContainer>
  );
};

interface TokenSwapProps {
  token: {
    symbol: string;
    logoURI: string;
    name: string;
    address: string;
    decimals: number;
    balance?: string;
  };
  advancedMode?: boolean;
  onDirectionChange?: () => void;
  allowAnyTokenSwap?: boolean; // If true, don't fix any token
  /** 'buy' = token is the "to" (receive) side; 'sell' = token is the "from" (pay) side */
  initialMode?: 'buy' | 'sell';
}

interface SwapInfo {
  ammKey: string;
  label: string;
  inputMint: string;
  outputMint: string;
  inAmount: string;
  outAmount: string;
  feeAmount: string;
  feeMint: string;
}

interface RoutePlan {
  swapInfo: SwapInfo;
  percent: number;
}

interface Quote {
  inputMint: string;
  inAmount: string;
  outputMint: string;
  outAmount: string;
  otherAmountThreshold: string;
  swapMode: string;
  slippageBps: number;
  platformFee: null | any;
  priceImpactPct: string;
  routePlan: RoutePlan[];
  contextSlot: number;
  timeTaken: number;
  swapUsdValue: string;
  simplerRouteUsed: boolean;
}

interface Fees {
  giftFee: string;
  transactionFee: string;
  totalFee: string;
}

interface Amounts {
  totalInputAmount: string;
  swapInputAmount: string;
  swapOutputAmount: string;
}

export interface SwapQuote {
  quote: Quote;
  fees: Fees;
  amounts: Amounts;
}

const percentageToBps = (percentage: string): number => {
  return Math.round(parseFloat(percentage || '0') * 100);
};

const bpsToPercentage = (bps: number): string => {
  return (bps / 100).toFixed(2);
};

const TokenSwap: React.FC<TokenSwapProps> = ({
  token,
  allowAnyTokenSwap = false,
  initialMode = 'buy',
}) => {
  const dispatch = useDispatch();
  const {
    executeUltraSwap,
    toTokenAmount,
    fromTokenAmount,
    embeddedWallet,
    externalWallet,
  } = useSolana();
  const usdcToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'USDC'),
  );
  const tokensWithBalance = useSelector((state) =>
    selectTokensWithUSDBalance(state),
  );
  const lastWalletRefreshTime = useSelector((state: any) =>
    selectLastRefreshTime(state),
  );
  const doCalculateCuaseFee = useFlag(UNLEASH_CALCULATE_CAUSE_FEE);
  const theme = useTheme();

  const { t } = useTranslation();
  const { login } = useCustomLogin();

  const { user } = usePrivy();

  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [isLoadingSwap, setIsLoadingSwap] = useState(false);
  const [causeFee, setCauseFee] = useState(0);
  const [quoteData, setQuoteData] = useState<SwapQuote | null>(null);
  const [selectedFromToken, setSelectedFromToken] = useState(null);
  const [selectedToToken, setSelectedToToken] = useState(null);
  const {
    data: priceData,
    isLoading: priceLoading,
    error: priceError,
  } = useGetTokenPriceQuery(token.address, {
    pollingInterval: 10000,
    skip: !token.address,
  });

  const isVets = token?.symbol === 'VETS';

  const slippageOptions = [
    { label: 'Auto', value: 'auto' },
    { label: '0.5%', value: '0.50' },
    { label: '1%', value: '1.00' },
    { label: '2%', value: '2.00' },
    { label: 'Custom', value: 'custom' },
  ];

  const defaultSlippage =
    import.meta.env.VITE_DEFAULT_SLIPPAGE === 'auto'
      ? 'auto'
      : bpsToPercentage(Number(import.meta.env.VITE_DEFAULT_SLIPPAGE));

  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [slippage, setSlippage] = useState<string>(defaultSlippage);
  const [customSlippage, setCustomSlippage] = useState<string>('');

  useEffect(() => {
    if (!slippageOptions.find((option) => option.value === defaultSlippage)) {
      setCustomSlippage(defaultSlippage);
    }
  }, [defaultSlippage]);

  const advancedMode = localStorage.getItem('advancedMode') === 'true';

  // Remove direction-based reset since users can now select any tokens

  useEffect(() => {
    if (doCalculateCuaseFee) {
      // Calculate cause fee based on USDC amount (when buying tokens with USDC)
      if (selectedFromToken?.symbol === 'USDC') {
        setCauseFee(parseFloat(fromAmount || '0') * 0.04);
      } else {
        setCauseFee(0);
      }
    }
  }, [fromAmount, toAmount, selectedFromToken?.symbol]);

  // Find token with highest balance for default "from" token
  // Normalize to have the same shape as selectedFromToken (value, label, etc.)
  const tokenWithHighestBalance = useMemo(() => {
    // Find token with highest USD balance
    const tokensWithBalanceArray = Object.values(tokensWithBalance || {});

    // If no balance data is loaded yet, don't return a fallback - wait for data
    if (tokensWithBalanceArray.length === 0) return null;

    // Sort tokens by USD balance (descending)
    const sortedTokens = tokensWithBalanceArray.sort((a, b) => {
      const aUSDBalance = (a.balance || 0) * (a.usdPrice || 0);
      const bUSDBalance = (b.balance || 0) * (b.usdPrice || 0);
      return bUSDBalance - aUSDBalance;
    });

    // Find the first token that doesn't match the fixed token (to avoid same from/to selection)
    const fixedTokenAddress = token?.address;
    let availableToken = sortedTokens.find(
      (t) => t.address !== fixedTokenAddress,
    );

    // Fall back to USDC only if all tokens match the fixed token (edge case)
    const rawToken = availableToken || usdcToken;

    // Return the raw token - all tokens have address property
    return rawToken;
  }, [usdcToken, tokensWithBalance, token?.address]);

  // Get the currently selected tokens (fallback to defaults if not set)
  const currentFromToken = selectedFromToken || tokenWithHighestBalance; // Default to highest balance token
  const currentToToken = selectedToToken || token; // Default to the page token for "to"

  // Helper to get address from token (handles both .address and .value properties)
  const getTokenAddress = (token: any) => token?.address || token?.value || '';

  // Get balance for the currently selected from token
  const currentFromTokenBalance = useSelector((state) =>
    currentFromToken
      ? selectTokenBalance(
          'solana',
          getTokenAddress(currentFromToken),
        )(state)
      : 0,
  );

  // Get wallet-specific balances for the from token
  const currentFromTokenEmbeddedBalance = useSelector((state) =>
    currentFromToken
      ? selectWalletTokenBalance(
          state,
          'solana',
          getTokenAddress(currentFromToken),
          'embedded',
        )
      : 0,
  );
  const currentFromTokenExternalBalance = useSelector((state) =>
    currentFromToken
      ? selectWalletTokenBalance(
          state,
          'solana',
          getTokenAddress(currentFromToken),
          'external',
        )
      : 0,
  );

  const showSkeleton = !lastWalletRefreshTime;

  const handleSwap = async () => {
    if (!user) {
      login();
      return;
    }

    if (!currentFromToken || !currentToToken) {
      console.error('From or to token not selected');
      return;
    }

    const amountNeeded = parseFloat(fromAmount);
    if (!amountNeeded) return;

    // Check if user has sufficient balance
    if (currentFromTokenBalance < amountNeeded) {
      // Only show add funds modal if trying to buy with USDC
      if (
        (currentFromToken as any)?.label === 'USDC' ||
        currentFromToken?.symbol === 'USDC'
      ) {
        sendEvent(EVENT_DEPOSIT_MODAL_OPENED, {
          from: TOKEN,
        });
        dispatch(
          openModal({
            name: 'addFunds',
            props: { amount: amountNeeded - currentFromTokenBalance },
          }),
        );
      } else {
        toast.error(
          `Insufficient ${(currentFromToken as any)?.label || currentFromToken?.symbol} balance`,
        );
      }
      return;
    }

    setIsLoadingSwap(true);

    try {
      // DEBUG: Log the amounts to see what's being passed
      console.log('[TokenSwap] Swap amounts:', {
        fromAmount,
        fromAmountType: typeof fromAmount,
        fromTokenDecimals: currentFromToken?.decimals,
        fromTokenSymbol: currentFromToken?.symbol,
        fromTokenAddress: getTokenAddress(currentFromToken),
        toTokenSymbol: currentToToken?.symbol,
        toTokenAddress: getTokenAddress(currentToToken),
      });

      // executeUltraSwap expects either:
      // - Raw amount as string without '.' (e.g., "2000000")
      // - Human-readable amount as number or string with '.' (e.g., 2 or "2.0")
      // It will convert if it's a number or has '.', otherwise uses as-is
      // Convert to raw amount ourselves to ensure correct decimals
      const rawAmount = toTokenAmount(fromAmount, currentFromToken?.decimals || 6);
      console.log('[TokenSwap] Converted raw amount:', {
        rawAmount,
        fromAmount,
        decimals: currentFromToken?.decimals || 6,
      });

      const response = await executeUltraSwap({
        inputMint: getTokenAddress(currentFromToken),
        outputMint: getTokenAddress(currentToToken),
        amount: rawAmount, // Already converted to raw token amount
      });
      sendEvent(EVENT_TOKEN_SWAP);
      if (response) {
        setFromAmount('');
        setToAmount('');
      }
    } catch (err) {
      // TODO: This is not being used - we should be able to remove it as the
      // error is being handled in the executeSwap function
      toast.error(
        `Failed to swap ${(currentFromToken as any)?.label || currentFromToken?.symbol}`,
      );
    } finally {
      setIsLoadingSwap(false);
    }
  };

  const handleSlippageChange = (value: string) => {
    setSlippage(value);
    setCustomSlippage('');
    hapticsImpactLight();
  };

  const handleCustomSlippageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (
      value === '' ||
      (!isNaN(Number(value)) && Number(value) >= 0 && Number(value) <= 50)
    ) {
      setCustomSlippage(value);
      if (value !== '') {
        setSlippage(value);
      }
    }
  };

  const handleFromTokenChange = (tokenOption: any) => {
    // TokenSwapInputs passes options - just store it as-is
    // We'll use getTokenAddress() helper to extract address correctly
    setSelectedFromToken(tokenOption);
  };

  const handleToTokenChange = (tokenOption: any) => {
    // TokenSwapInputs passes options - just store it as-is
    setSelectedToToken(tokenOption);
  };

  // When initialMode is 'sell', set token as "from" and USDC as "to"
  useEffect(() => {
    if (initialMode === 'sell' && token && usdcToken) {
      setSelectedFromToken({
        value: token.address,
        label: token.symbol,
        symbol: token.symbol,
        name: token.name,
        logoURI: token.logoURI,
        decimals: token.decimals,
        address: token.address,
      } as any);
      setSelectedToToken({
        value: usdcToken.address,
        label: usdcToken.symbol,
        symbol: usdcToken.symbol,
        name: usdcToken.name,
        logoURI: usdcToken.logoURI,
        decimals: usdcToken.decimals,
        address: usdcToken.address,
      } as any);
    }
  }, [initialMode, token?.address, usdcToken?.address]);

  // Update selected tokens when balance data becomes available (buy mode only)
  useEffect(() => {
    if (initialMode !== 'buy') return;
    // Only auto-select if no token is currently selected and balance data is available
    if (!selectedFromToken && tokenWithHighestBalance) {
      // Normalize to match TokenSwapInputs option shape (has 'value' property)
      setSelectedFromToken({
        value: tokenWithHighestBalance.address,
        label: tokenWithHighestBalance.symbol,
        symbol: tokenWithHighestBalance.symbol,
        name: tokenWithHighestBalance.name,
        logoURI: tokenWithHighestBalance.logoURI,
        decimals: tokenWithHighestBalance.decimals,
        address: tokenWithHighestBalance.address, // Keep address for consistency
      } as any);
    }
  }, [initialMode, selectedFromToken, tokenWithHighestBalance]);

  const renderAdvancedSettings = () => {
    if (!advancedMode) return null;

    return (
      <>
        <AdvancedSettingsButton
          onClick={() => {
            hapticsImpactLight();
            setShowAdvancedSettings(!showAdvancedSettings);
          }}
        >
          <ParagraphSmallText style={{ color: theme.text.secondary }}>
            Slippage
          </ParagraphSmallText>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ParagraphSmallText style={{ color: theme.text.secondary }}>
              {slippage === 'auto' ? 'Auto' : `${slippage}%`}
            </ParagraphSmallText>
            <img
              src={
                showAdvancedSettings ? '/chevron-up.svg' : '/chevron-down.svg'
              }
              alt='toggle'
              style={{ width: '12px', height: '12px' }}
            />
          </div>
        </AdvancedSettingsButton>

        {showAdvancedSettings && (
          <AdvancedSettingsContent>
            <SlippageButtons>
              {slippageOptions.map((option) =>
                option.value === 'custom' ? (
                  <div
                    key='custom'
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <CustomSlippageInput
                      value={customSlippage}
                      onChange={handleCustomSlippageChange}
                      $active={slippage === customSlippage}
                    />
                    <span style={{ marginLeft: '4px' }}>%</span>
                  </div>
                ) : (
                  <SlippageButton
                    key={option.value}
                    $active={slippage === option.value && !customSlippage}
                    onClick={() => handleSlippageChange(option.value)}
                  >
                    {option.label}
                  </SlippageButton>
                ),
              )}
            </SlippageButtons>
            {quoteData?.quote?.otherAmountThreshold && (
              <div style={{ marginTop: '12px' }}>
                <ParagraphSmallText style={{ color: theme.text.secondary }}>
                  Minimum output:{' '}
                  {fromTokenAmount(
                    quoteData.quote.otherAmountThreshold,
                    currentToToken?.decimals || 6,
                  ).toFixed(4)}{' '}
                  {currentToToken?.symbol || 'USDC'}
                </ParagraphSmallText>
              </div>
            )}
          </AdvancedSettingsContent>
        )}
      </>
    );
  };

  return showSkeleton ? (
    <TokenSwapSkeleton />
  ) : (
    <div>
      <TokenSwapInputs
        fromAmount={fromAmount}
        defaultFromToken={
          initialMode === 'sell'
            ? token?.address
            : tokenWithHighestBalance?.address || usdcToken?.address
        }
        defaultToToken={
          initialMode === 'sell' ? usdcToken?.address : token?.address
        }
        fixedTokenAddress={allowAnyTokenSwap ? undefined : token?.address} // Fix the page token so it can't be changed (unless allowAnyTokenSwap is true)
        onFromAmountChange={setFromAmount}
        onToAmountChange={setToAmount}
        onFromTokenChange={handleFromTokenChange}
        onToTokenChange={handleToTokenChange}
        disabled={isLoadingSwap || priceLoading || !user}
        slippageBps={
          slippage === 'auto' ? 'auto' : String(percentageToBps(slippage))
        }
        onQuoteChange={setQuoteData}
      />
      {priceLoading && (
        <PriceInfo>
          <BodySmall>{t('trade.calculatingPrice')}</BodySmall>
        </PriceInfo>
      )}

      {priceData && !priceLoading && !priceError && (
        <PriceInfo>
          <ParagraphSmallText style={{ color: theme.text.secondary }}>
            {t('trade.rate')}
          </ParagraphSmallText>
          <ParagraphSmallText style={{ color: theme.text.secondary }}>
            1 {token.symbol} = ${' '}
            {currentFromToken?.symbol === 'USDC'
              ? `${formatTokenPrice(priceData.buyPrice)}`
              : `${formatTokenPrice(priceData.sellPrice)}`}
          </ParagraphSmallText>
        </PriceInfo>
      )}
      {isVets && (
        <PriceInfo>
          <ParagraphSmallText style={{ color: theme.text.secondary }}>
            {t('trade.causeFee')}
          </ParagraphSmallText>
          <ParagraphSmallText style={{ color: theme.text.secondary }}>
            $ {causeFee.toFixed(4)}
          </ParagraphSmallText>
        </PriceInfo>
      )}

      {renderAdvancedSettings()}
      <Button
        fullWidth
        loading={isLoadingSwap}
        onClick={handleSwap}
        disabled={
          // If there is a user, run all validation checks
          !!user &&
          // Validate from amount is number greater than 0
          (!parseFloat(fromAmount) ||
            // Validate a swap is not already in progress
            isLoadingSwap ||
            // Validate to amount is not empty
            !toAmount)
        }
      >
        {!user ? t('trade.login') : t('trade.trade')}
      </Button>
    </div>
  );
};

export default TokenSwap;

const SkeletonOuter = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const TokenSwapSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const cardWidth = isMobile ? 332 : 520;
  const padding = isMobile ? 16 : 24;
  const innerWidth = cardWidth - padding * 2;
  const rowHeight = isMobile ? 116 : 126;
  const gap = isMobile ? 14 : 18;
  const chipWidth = isMobile ? 108 : 132;
  const chipHeight = isMobile ? 44 : 48;
  const amountWidth = Math.round(innerWidth * (isMobile ? 0.55 : 0.6));
  const smallTextWidth = Math.round(innerWidth * 0.35);

  const firstRowY = padding;
  const secondRowY = firstRowY + rowHeight + gap;
  const rateY = secondRowY + rowHeight + gap;
  const buttonHeight = isMobile ? 56 : 60;
  const buttonY = rateY + 38;
  const cardHeight = buttonY + buttonHeight + padding;

  const chipX = padding + innerWidth - chipWidth - 20;
  const chipLabelY = firstRowY + 18;
  const amountY = firstRowY + 46;
  const balanceY = amountY + 44;
  const maxChipY = balanceY - 2;

  const swapSize = isMobile ? 44 : 52;
  const swapX = padding + innerWidth / 2 - swapSize / 2;
  const swapY = firstRowY + rowHeight - swapSize / 2;

  return (
    <SkeletonOuter>
      <ContentLoader
        speed={2}
        backgroundColor={theme.background.secondary}
        foregroundColor={theme.special.skeleton}
        width={cardWidth}
        height={cardHeight}
        viewBox={`0 0 ${cardWidth} ${cardHeight}`}
        style={{ width: '100%', maxWidth: `${cardWidth}px` }}
      >
        <rect x='0' y='0' rx='24' ry='24' width={cardWidth} height={cardHeight} />

        <rect
          x={padding}
          y={firstRowY}
          rx='28'
          ry='28'
          width={innerWidth}
          height={rowHeight}
        />
        <rect
          x={padding + 20}
          y={chipLabelY}
          rx='6'
          ry='6'
          width='72'
          height='16'
        />
        <rect
          x={padding + 20}
          y={amountY}
          rx='12'
          ry='12'
          width={amountWidth}
          height='34'
        />
        <rect
          x={padding + 20}
          y={balanceY}
          rx='9'
          ry='9'
          width={smallTextWidth}
          height='20'
        />
        <rect
          x={chipX}
          y={chipLabelY - 4}
          rx={chipHeight / 2}
          ry={chipHeight / 2}
          width={chipWidth}
          height={chipHeight}
        />
        <rect
          x={chipX}
          y={maxChipY}
          rx='10'
          ry='10'
          width='60'
          height='20'
        />
        <rect
          x={chipX + chipWidth - 52}
          y={maxChipY - 2}
          rx='10'
          ry='10'
          width='52'
          height='24'
        />

        <rect
          x={padding}
          y={secondRowY}
          rx='28'
          ry='28'
          width={innerWidth}
          height={rowHeight}
        />
        <rect
          x={padding + 20}
          y={secondRowY + 18}
          rx='6'
          ry='6'
          width='92'
          height='16'
        />
        <rect
          x={padding + 20}
          y={secondRowY + 46}
          rx='12'
          ry='12'
          width={amountWidth}
          height='34'
        />
        <rect
          x={padding + 20}
          y={secondRowY + 90}
          rx='9'
          ry='9'
          width='92'
          height='18'
        />
        <rect
          x={chipX}
          y={secondRowY + 22}
          rx={chipHeight / 2}
          ry={chipHeight / 2}
          width={chipWidth}
          height={chipHeight}
        />

        <rect
          x={swapX}
          y={swapY}
          rx='16'
          ry='16'
          width={swapSize}
          height={swapSize}
        />

        <rect
          x={padding}
          y={rateY}
          rx='8'
          ry='8'
          width='56'
          height='16'
        />
        <rect
          x={padding + innerWidth - 168}
          y={rateY}
          rx='8'
          ry='8'
          width='168'
          height='16'
        />

        <rect
          x={padding}
          y={buttonY}
          rx={buttonHeight / 2}
          ry={buttonHeight / 2}
          width={innerWidth}
          height={buttonHeight}
        />
      </ContentLoader>
    </SkeletonOuter>
  );
};
